/**
 * Per-yard garden food state — corn (Hubert) and tomato (Holly Springs).
 *
 * Module map survives Friends switching, like rabbit and firepit state.
 */

import type { SpawnPoint } from '@/world-prototype/types';
import {
  GARDEN_COOLDOWN_MS,
  gardenFoodKindForYard,
  isGardenFoodYard,
  type GardenFoodKind,
} from '@/world-prototype/interaction/gardenFood';

export type GardenFoodPhase =
  | 'idle'
  | 'harvesting'
  | 'throwing'
  | 'landed'
  | 'targeted'
  | 'eating'
  | 'cooldown';

export interface GardenFoodState {
  kind: GardenFoodKind;
  phase: GardenFoodPhase;
  x: number;
  y: number;
  rotation: number;
  cooldownMs: number;
}

const states = new Map<string, GardenFoodState>();

function createState(yardId: string): GardenFoodState {
  const kind = gardenFoodKindForYard(yardId);
  if (!kind) {
    throw new Error(`No garden food for yard ${yardId}`);
  }
  return {
    kind,
    phase: 'idle',
    x: 0,
    y: 0,
    rotation: 0,
    cooldownMs: 0,
  };
}

export function gardenFoodStateForYard(
  yardId: string,
): GardenFoodState | null {
  if (!isGardenFoodYard(yardId)) return null;
  let state = states.get(yardId);
  if (!state) {
    state = createState(yardId);
    states.set(yardId, state);
  }
  return state;
}

/** True when the player may start a new harvest. */
export function canHarvestGarden(yardId: string): boolean {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return false;
  return state.phase === 'idle';
}

export function beginGardenHarvest(yardId: string): boolean {
  const state = gardenFoodStateForYard(yardId);
  if (!state || state.phase !== 'idle') return false;
  state.phase = 'harvesting';
  return true;
}

export function beginGardenThrow(yardId: string): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return;
  state.phase = 'throwing';
}

export function landGardenFood(
  yardId: string,
  point: SpawnPoint,
  rotation: number,
): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return;
  state.phase = 'landed';
  state.x = point.x;
  state.y = point.y;
  state.rotation = rotation;
}

export function markGardenFoodTargeted(yardId: string): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state || state.phase !== 'landed') return;
  state.phase = 'targeted';
}

export function beginGardenEat(yardId: string): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return;
  state.phase = 'eating';
}

export function finishGardenEat(yardId: string): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return;
  state.phase = 'cooldown';
  state.cooldownMs = GARDEN_COOLDOWN_MS;
}

export function tickGardenFoodCooldown(yardId: string, dtMs: number): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state || state.phase !== 'cooldown') return;
  state.cooldownMs = Math.max(0, state.cooldownMs - dtMs);
  if (state.cooldownMs <= 0) {
    state.phase = 'idle';
  }
}

/** Food on the ground waiting for the rabbit. */
export function landedGardenFoodPoint(yardId: string): SpawnPoint | null {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return null;
  if (
    state.phase === 'landed' ||
    state.phase === 'targeted' ||
    state.phase === 'eating'
  ) {
    return { x: state.x, y: state.y };
  }
  return null;
}

export function isGardenFoodAvailableToRabbit(yardId: string): boolean {
  const state = gardenFoodStateForYard(yardId);
  return state?.phase === 'landed' || state?.phase === 'targeted';
}

/** Landed, targeted, or mid-eat — rabbit ctx must keep callbacks through eat. */
export function isGardenFoodActiveForRabbit(yardId: string): boolean {
  const state = gardenFoodStateForYard(yardId);
  return (
    state?.phase === 'landed' ||
    state?.phase === 'targeted' ||
    state?.phase === 'eating'
  );
}

export function resetGardenFoodStatesForTests(): void {
  states.clear();
}

/**
 * Recover from interrupted harvest/throw/eat sequences (HMR, desynced rabbit).
 * Landed food waiting for a healthy rabbit is left alone.
 */
export function reconcileGardenFoodIdleState(yardId: string): void {
  const state = gardenFoodStateForYard(yardId);
  if (!state) return;
  if (
    state.phase === 'harvesting' ||
    state.phase === 'throwing' ||
    state.phase === 'eating'
  ) {
    state.phase = 'idle';
  }
}
